import { useState } from 'react';
import { Download, Loader2, Smartphone, FlaskConical } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { downloadService } from '../services';
import type { ApkVariantDto } from '../types';

function formatFileSize(bytes: number | null): string {
  if (bytes === null) return 'Unknown size';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface ApkDownloadCardProps {
  variant: ApkVariantDto;
}

export function ApkDownloadCard({ variant }: ApkDownloadCardProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const isTest = variant.variant === 'test';

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const { blob, fileName } = await downloadService.downloadApk(
        variant.variant,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${variant.label} APK download started.`);
    } catch {
      toast.error('Download failed. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Card className="w-full max-w-sm">
      <CardHeader>
        <div className="flex items-center gap-2">
          {isTest ? (
            <FlaskConical className="h-5 w-5 text-warning" />
          ) : (
            <Smartphone className="h-5 w-5 text-info" />
          )}
          <CardTitle className="text-lg">{variant.label}</CardTitle>
          {isTest && <Badge variant="warning">Pre-release</Badge>}
        </div>
        <CardDescription>Version {variant.version}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="text-sm text-muted-foreground space-y-1">
          <p>File: {variant.fileName}</p>
          <p>Size: {formatFileSize(variant.fileSizeBytes)}</p>
        </div>
      </CardContent>
      <CardFooter>
        <Button
          onClick={handleDownload}
          disabled={!variant.available || isDownloading}
          className="w-full"
        >
          {isDownloading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Downloading...
            </>
          ) : !variant.available ? (
            'Not Available'
          ) : (
            <>
              <Download className="mr-2 h-4 w-4" /> Download APK
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}
